import { PLAYER } from "../../../common/constants.ts";
import { getAll, idb } from "../index.ts";
import { mergeByPk } from "./helpers.ts";
import { g, helpers } from "../../util/index.ts";
import type { GetCopyType, Player } from "../../../common/types.ts";
import { type IDBPDatabase } from "@dumbmatter/idb";
import type { LeagueDB } from "../connectLeague.ts";

export const getPlayersActiveSeason = async (
	league: IDBPDatabase<LeagueDB>,
	season: number,
) => {
	const index = league
		.transaction("players")
		.store.index("draft.year, retiredYear");

	const players: Player[] = [];

	// -1 is because players drafted the year won't play until next year
	const range = IDBKeyRange.bound([-Infinity, season], [season - 1, Infinity]);
	for await (const cursor of index.iterate(range)) {
		const [draftYear2, retiredYear] = cursor.key;

		// https://gist.github.com/inexorabletash/704e9688f99ac12dd336
		if (retiredYear < season) {
			cursor.continue([draftYear2, season]);
		} else {
			players.push(cursor.value);
		}
	}

	return players;
};

const getCopies = async (
	{
		pid,
		pids,
		retiredYear,
		activeAndRetired,
		activeSeason,
		draftYear,
		hof,
		note,
		statsTid,
		tid,
		watch,
		filter = () => true,
	}: {
		pid?: number;
		pids?: number[];
		retiredYear?: number;
		activeAndRetired?: boolean;
		activeSeason?: number;
		draftYear?: number;
		hof?: boolean;
		note?: boolean;
		statsTid?: number;
		tid?: [number, number] | number;
		watch?: boolean;
		filter?: (p: Player) => boolean;
	} = {},
	type?: GetCopyType,
): Promise<Player[]> => {
	if (pids?.length === 1) {
		pid = pids[0];
	}

	if (pid !== undefined) {
		const p = await idb.cache.players.get(pid);
		if (p) {
			return [type === "noCopyCache" ? p : helpers.deepCopy(p)];
		}

		const p2 = await idb.league.get("players", pid);
		if (p2) {
			return [p2];
		}

		return [];
	}

	if (pids !== undefined) {
		if (pids.length === 0) {
			return [];
		}

		const sortedPids = pids.toSorted((a, b) => a - b);
		const fromDB: Player[] = [];

		// https://gist.github.com/inexorabletash/704e9688f99ac12dd336
		const store = idb.league.transaction("players").store;
		const range = IDBKeyRange.bound(sortedPids[0], sortedPids.at(-1));
		let i = 0;
		for await (const cursor of store.iterate(range)) {
			const p = cursor.value;

			const targetPid = sortedPids[i];
			if (p.pid === targetPid) {
				fromDB.push(p);
				i += 1;
			} else if (sortedPids.includes(p.pid)) {
				// This is a weird edge case. If you look for pids [1, 2] and 1 is does not exist in the database but 2 does, then the cursor iteration looking for 1 will skip ahead to the next key and find player 2. But then calling `cursor.continue` below, the parameter needs to be greater than the current primary key, so we need to make sure we advance `i` to the next value beyond 2
				fromDB.push(p);
				i = sortedPids.indexOf(p.pid) + 1;
			} else {
				// This is similar to the above case, except if the next key is not in sortedPids. For example if we're looking for [1, 3] and 1 is not in the database but 2 is, then `p` is player 2. But this could also require us to skip ahead multiple times, like if sortedPids is [1, 2, 3, 10] and the first 3 are all not in the database, then the first player returned will be 4, and then the next request needs to be for 10 and will error if it is for 2 (cursor.continue cannot go backwards).
				i = sortedPids.findIndex((pid) => pid > p.pid);
				if (i === -1) {
					break;
				}
			}

			if (i >= sortedPids.length) {
				break;
			}

			cursor.continue(sortedPids[i]);
		}

		const merged = mergeByPk(
			fromDB,
			(await idb.cache.players.getAll()).filter((p) => pids.includes(p.pid)),
			"players",
			type,
		);

		const sorted = [];
		for (const pid of pids) {
			const p = merged.find((p2) => p2.pid === pid);
			if (p) {
				sorted.push(p);
			}
		}

		return sorted;
	}

	if (retiredYear !== undefined) {
		const fromDB: Player[] = [];
		const index = idb.league
			.transaction("players")
			.store.index("draft.year, retiredYear");
		for await (const cursor of index) {
			const [draftYear, currentRetiredYear] = cursor.key;

			// https://gist.github.com/inexorabletash/704e9688f99ac12dd336
			if (currentRetiredYear < retiredYear) {
				cursor.continue([draftYear, retiredYear]);
			} else if (currentRetiredYear > retiredYear) {
				cursor.continue([draftYear + 1, retiredYear]);
			} else {
				fromDB.push(cursor.value);
			}
		}

		// Get all from cache, and filter later, in case cache differs from database
		return mergeByPk(
			fromDB,
			await idb.cache.players.indexGetAll("playersByTid", PLAYER.RETIRED),
			"players",
			type,
		).filter((p) => p.retiredYear === retiredYear);
	}

	if (tid !== undefined) {
		if (Array.isArray(tid)) {
			const [minTid, maxTid] = tid; // Avoid PLAYER.RETIRED, since those aren't in cache

			if (
				minTid === PLAYER.RETIRED ||
				maxTid === PLAYER.RETIRED ||
				(minTid < PLAYER.RETIRED && maxTid > PLAYER.RETIRED)
			) {
				throw new Error("Not implemented");
			}
		}

		// This works if tid is a number or [min, max]
		const fromDB = (
			await idb.cache.players.indexGetAll("playersByTid", tid)
		).filter(filter);
		return type === "noCopyCache" ? fromDB : helpers.deepCopy(fromDB);
	}

	if (activeAndRetired === true) {
		// All except draft prospects
		return mergeByPk(
			[].concat(
				// @ts-expect-error
				await getAll(
					idb.league.transaction("players").store.index("tid"),
					PLAYER.RETIRED,
					filter,
				),
				await idb.league
					.transaction("players")
					.store.index("tid")
					.getAll(IDBKeyRange.lowerBound(PLAYER.FREE_AGENT)),
			),
			[].concat(
				// @ts-expect-error
				await idb.cache.players.indexGetAll("playersByTid", PLAYER.RETIRED),
				await idb.cache.players.indexGetAll("playersByTid", [
					PLAYER.FREE_AGENT,
					Infinity,
				]),
			),
			"players",
			type,
		).filter(filter);
	}

	if (activeSeason !== undefined) {
		let proceed = true;
		if (statsTid !== undefined) {
			// If statsTid and activeSeason are both defined, use activeSeason rather thatn statsTid based on number of seasons/teams
			const numTeams = g.get("numTeams");
			const numSeasons = g.get("season") - g.get("startingSeason");

			// Factor is based on testing when this actually gets faster
			if (5 * numTeams > numSeasons) {
				proceed = false;
			}
		}

		if (proceed) {
			const fromDB = await getPlayersActiveSeason(idb.league, activeSeason);

			return mergeByPk(
				fromDB,
				([] as Player[])
					.concat(
						await idb.cache.players.indexGetAll("playersByTid", PLAYER.RETIRED),
						await idb.cache.players.indexGetAll("playersByTid", [
							PLAYER.FREE_AGENT,
							Infinity,
						]),
					)
					.filter(
						(p) =>
							p.draft.year < activeSeason &&
							p.retiredYear >= activeSeason &&
							(statsTid === undefined || p.statsTids.includes(statsTid)),
					),
				"players",
				type,
			);
		}
	}

	if (hof) {
		return mergeByPk(
			await getAll(
				idb.league.transaction("players").store.index("hof"),
				1,
				filter,
			),
			(await idb.cache.players.getAll()).filter((p) => p.hof === 1),
			"players",
			type,
		).filter(filter);
	}

	if (draftYear !== undefined) {
		return mergeByPk(
			await idb.league
				.transaction("players")
				.store.index("draft.year, retiredYear")
				.getAll(
					IDBKeyRange.bound([draftYear, -Infinity], [draftYear, Infinity]),
				),
			(
				await idb.cache.players.indexGetAll("playersByTid", [
					PLAYER.RETIRED,
					Infinity,
				])
			).filter((p) => p.draft.year === draftYear),
			"players",
			type,
		);
	}

	if (statsTid !== undefined) {
		return mergeByPk(
			await getAll(
				idb.league.transaction("players").store.index("statsTids"),
				statsTid,
			),
			([] as Player[])
				.concat(
					await idb.cache.players.indexGetAll("playersByTid", PLAYER.RETIRED),
					await idb.cache.players.indexGetAll("playersByTid", [
						PLAYER.FREE_AGENT,
						Infinity,
					]),
				)
				.filter((p) => p.statsTids.includes(statsTid)),
			"players",
			type,
		);
	}

	if (note) {
		return mergeByPk(
			// undefined for key returns all of the players with noteBool, since the ones without noteBool are not included in this index
			await getAll(
				idb.league.transaction("players").store.index("noteBool"),
				undefined,
				filter,
			),
			await idb.cache.players.getAll(),
			"players",
			type,
		).filter((p) => p.noteBool === 1 && filter(p));
	}

	if (watch) {
		return mergeByPk(
			// undefined for key returns all of the players with values, since the ones with without watch are not included in this index
			await getAll(
				idb.league.transaction("players").store.index("watch"),
				undefined,
				filter,
			),
			await idb.cache.players.getAll(),
			"players",
			type,
		).filter((p) => p.watch !== undefined && filter(p));
	}

	return mergeByPk(
		await getAll(idb.league.transaction("players").store, undefined, filter),
		await idb.cache.players.getAll(),
		"players",
		type,
	).filter(filter);
};

export default getCopies;
